import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  unwrapMaybeOr,
  type Maybe,
  matchMaybe,
} from "./maybe";
import {
  cloudApiBaseUrl,
  configuredCloudApiUrl,
  currentCloudBuildMode,
  fallbackCloudApiBase,
  resolveCloudApiBase,
} from "./cloudApiBase";
import { isTauri } from "./runtime";
import { isCloudRoot } from "./platform/projectRoot";
import { trimmedMaybe, trimmedOrEmpty } from "./trimmedString";

/** UUID project ids in `/editor/<uuid>/…` host paths. */
const EDITOR_PROJECT_PATH =
  /^\/editor\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;

/** Share token in `/reader/:token/…` host paths. */
const READER_TOKEN_PATH = /^\/reader\/([^/]+)(?:\/|$)/;

/** Legacy pathname tabs that used to live under `/editor/<tab>`. */
const LEGACY_EDITOR_TABS = new Set([
  "manuscript",
  "storyboard",
  "notes",
  "database",
  "trash",
  "search",
]);

/** True when the SPA is mounted under `/editor` on the cloud app origin. */
export function isCloudEditorHosted(): boolean {
  if (typeof window === "undefined" || isTauri()) return false;
  return window.location.pathname.includes("/editor");
}

/** True when the SPA is mounted under `/reader` (public/share read). */
export function isCloudReaderHosted(): boolean {
  if (typeof window === "undefined" || isTauri()) return false;
  return window.location.pathname.startsWith("/reader/");
}

/** Owner editor or share reader on the cloud app origin. */
export function isCloudAppHosted(): boolean {
  return isCloudEditorHosted() || isCloudReaderHosted();
}

/** Account page URL for the cloud editor chrome (back to projects). */
export function cloudAccountUrl(): string {
  if (isCloudAppHosted()) {
    return `${window.location.origin}/account`;
  }
  return `${cloudApiBaseUrl()}/account`;
}

/** Sign-in page when the cloud editor is opened without a session. */
export function cloudSignInUrl(): string {
  if (isCloudAppHosted()) {
    return `${window.location.origin}/sign-in`;
  }
  return `${cloudApiBaseUrl()}/sign-in`;
}

/** API origin for the hosted cloud editor (Phoenix), not the Vite dev port. */
export function cloudEditorApiOrigin(): string {
  const envM = configuredCloudApiUrl();
  if (isJust(envM)) return resolveCloudApiBase(envM, currentCloudBuildMode());

  if (typeof window !== "undefined" && isCloudAppHosted()) {
    const { origin, port } = window.location;
    if (port === "4000" || port === "1422" || port === "") return origin;
  }

  return fallbackCloudApiBase(currentCloudBuildMode());
}

/** Project id from `/editor/<uuid>/…` (canonical hosted shape). */
export function cloudProjectIdFromPathname(
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "",
): Maybe<string> {
  const match = pathname.match(EDITOR_PROJECT_PATH);
  return trimmedMaybe(match?.[1]);
}

/** Share token from `/reader/:token/…`. */
export function activeShareToken(
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "",
): Maybe<string> {
  const match = pathname.match(READER_TOKEN_PATH);
  const tokenM = trimmedMaybe(match?.[1]);
  if (isNothing(tokenM) || tokenM.value === "assets") {
    return nothing;
  }
  return just(decodeURIComponent(tokenM.value));
}

export type ShareSessionRole = "read" | "comment" | "edit";

export function parseShareSessionRole(value: unknown): Maybe<ShareSessionRole> {
  if (value === "read" || value === "comment" || value === "edit") {
    return just(value);
  }
  return nothing;
}

/**
 * Cookie-backed ACL role for the `/reader` SPA (#101).
 * Nothing when the session endpoint is missing or denied.
 */
export async function fetchShareSessionRole(): Promise<
  Maybe<ShareSessionRole>
> {
  const tokenM = activeShareToken();
  if (isNothing(tokenM)) return nothing;
  try {
    const res = await fetch(
      `${cloudEditorApiOrigin()}/reader/${encodeURIComponent(tokenM.value)}/session`,
      { credentials: "include" },
    );
    if (res.ok === false) return nothing;
    const body = (await res.json()) as { role?: unknown };
    return parseShareSessionRole(body.role);
  } catch {
    return nothing;
  }
}

function cloudProjectIdFromQueryParam(raw: unknown): Maybe<string> {
  const rawM = trimmedMaybe(typeof raw === "string" ? raw : "");
  return matchMaybe(rawM, {
    nothing: () => nothing,
    just: (value) => {
      if (isCloudRoot(value) === false) return nothing;
      return just(value.slice("cloud://".length));
    },
  });
}

/**
 * Cloud project id for the hosted editor.
 * Prefer `/editor/<uuid>/…`, then legacy `?project=cloud://…` (search or hash).
 * Reader hosts do not expose an owner project id here.
 */
export function activeCloudProjectId(): Maybe<string> {
  if (typeof window === "undefined") return nothing;
  if (isCloudReaderHosted()) return nothing;

  const fromPath = cloudProjectIdFromPathname(window.location.pathname);
  if (isJust(fromPath)) return fromPath;

  const fromSearch = cloudProjectIdFromQueryParam(
    new URLSearchParams(window.location.search).get("project"),
  );
  if (isJust(fromSearch)) return fromSearch;

  const hash = window.location.hash;
  const qIndex = hash.indexOf("?");
  if (qIndex !== -1) {
    const fromHash = cloudProjectIdFromQueryParam(
      new URLSearchParams(hash.slice(qIndex + 1)).get("project"),
    );
    if (isJust(fromHash)) return fromHash;
  }
  return nothing;
}

/** Cloud project id from a `cloud://` root path (e.g. session state). */
export function cloudProjectIdFromRoot(rootPath: Maybe<string>): Maybe<string> {
  return matchMaybe(rootPath, {
    nothing: () => nothing,
    just: (root) => {
      if (isCloudRoot(root) === false) return nothing;
      return just(root.slice("cloud://".length));
    },
  });
}

/** `cloud://<id>` root for the active hosted project, if any. */
export function activeCloudProjectRoot(): Maybe<string> {
  return matchMaybe<string, Maybe<string>>(activeCloudProjectId(), {
    nothing: (): Maybe<string> => nothing,
    just: (id) => just(`cloud://${id}`),
  });
}

/** Resolved cloud project id for invoke + channel subscriptions. */
export function resolvedCloudProjectId(
  rootPath: Maybe<string> = nothing,
): Maybe<string> {
  const fromHost = activeCloudProjectId();
  if (isJust(fromHost)) return fromHost;
  return cloudProjectIdFromRoot(rootPath);
}

/**
 * If this is a legacy `/editor/<tab>?project=cloud://…` URL, replace it with
 * `/editor/<uuid>/#/<hash>`. Returns true when a redirect was kicked off.
 */
export function maybeRedirectLegacyCloudEditorUrl(): boolean {
  if (typeof window === "undefined" || isTauri()) return false;
  if (isCloudReaderHosted()) return false;

  const { pathname, search, hash } = window.location;
  const idM = cloudProjectIdFromQueryParam(
    new URLSearchParams(search).get("project"),
  );
  if (isNothing(idM)) return false;
  const id = idM.value;

  const match = pathname.match(/^\/editor(?:\/(.*))?$/);
  if (match === null) return false;
  const restM = toMaybe(match[1]);
  const rest = (isNothing(restM) ? "" : restM.value).replace(/\/$/, "");
  const fromPath = cloudProjectIdFromPathname(pathname);
  if (isJust(fromPath)) return false;

  let hashPath = trimmedOrEmpty(hash.replace(/^#/, ""));
  if (hashPath.length === 0) hashPath = "/";
  if (hash.length === 0 || hash === "#" || hash === "#/") {
    if (rest.length === 0 || rest === "manuscript") hashPath = "/";
    else if (LEGACY_EDITOR_TABS.has(rest.split("/")[0] ?? "")) {
      hashPath = rest === "manuscript" ? "/" : `/${rest}`;
    } else {
      hashPath = "/";
    }
  }

  const nextHash = hashPath.startsWith("/") ? hashPath : `/${hashPath}`;
  const base = `${window.location.origin}/editor/${id}/`;
  window.location.replace(nextHash === "/" ? base : `${base}#${nextHash}`);
  return true;
}

function hashRouteOrManuscript(hash: string): Maybe<string> {
  const hashPath = hash.replace(/^#/, "");
  if (hashPath.length > 0 && hashPath !== "/") {
    return just(unwrapMaybeOr(trimmedMaybe(hashPath.split("?")[0]), "/"));
  }
  return just("/");
}

/**
 * Initial in-app hash route when the web build boots on a cloud editor URL.
 * Host path is `/editor/<uuid>/` or `/reader/:token/`; Vue owns only the hash.
 * Nothing when this boot is not a cloud editor/reader host.
 */
export function initialCloudEditorRoute(): Maybe<string> {
  if (typeof window === "undefined" || isTauri()) return nothing;

  const { pathname, hash } = window.location;
  if (isCloudReaderHosted()) {
    return hashRouteOrManuscript(hash);
  }

  if (pathname.includes("/editor") === false) return nothing;

  const pathId = cloudProjectIdFromPathname(pathname);
  if (isJust(pathId)) {
    return hashRouteOrManuscript(hash);
  }

  const match = pathname.match(/^\/editor(?:\/(.*))?$/);
  const rest = (match?.[1] ?? "").replace(/\/$/, "");
  if (rest.length === 0 || rest === "manuscript") return just("/");
  const tab = rest.split("/")[0] ?? "";
  if (LEGACY_EDITOR_TABS.has(tab)) {
    return just(rest === "manuscript" ? "/" : `/${rest}`);
  }
  return just("/");
}
